import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PhoneCall, PhoneOff, Radio, ShieldAlert, Wrench, Play, UserCheck } from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { api } from '../../context/AuthContext';

const REFRESH_MS = 5000;

function fmtWhen(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

function fmtDuration(seconds) {
  if (!seconds || seconds < 0) return '—';
  const m = Math.floor(seconds / 60), s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function CrmLiveCalls() {
  const [calls, setCalls] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [monitorEvents, setMonitorEvents] = useState([]);
  const [error, setError] = useState('');
  const wsRef = useRef(null);
  const scrollRef = useRef(null);

  // Poll the call list so a new inbound call surfaces within 5s.
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get('/voice/calls');
        if (alive) setCalls(data.calls || []);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || 'Failed to load calls');
      }
    };
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // Open a monitor WS whenever the selection changes.
  useEffect(() => {
    if (wsRef.current) { try { wsRef.current.close(); } catch {} wsRef.current = null; }
    setMonitorEvents([]);
    setSelectedDetail(null);
    if (!selectedId) return;
    (async () => {
      try {
        const { data } = await api.get(`/voice/calls/${selectedId}`);
        setSelectedDetail(data);
        if (Array.isArray(data.transcript)) {
          setMonitorEvents(data.transcript.map((t) => ({ type: `${t.role}_transcript`, text: t.text, at: t.at })));
        }
      } catch (e) {
        setError('Failed to load call detail');
        return;
      }
      const token = localStorage.getItem('access_token') || '';
      const base = (process.env.REACT_APP_BACKEND_URL || window.location.origin)
        .replace(/^http/, 'ws');
      const url = `${base}/api/voice/monitor/${selectedId}?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(url);
      ws.onmessage = (m) => {
        try {
          const evt = JSON.parse(m.data);
          setMonitorEvents((prev) => [...prev, evt].slice(-500));
          // Mid-call recognition: hydrate the call row + selected detail
          // as soon as the bridge tells us this is a returning caller,
          // so the pill appears BEFORE the 5s poll catches up.
          if (evt.type === 'returning_caller') {
            setSelectedDetail((prev) => prev ? { ...prev, returning_caller: true,
                                                 client_id: evt.client_id } : prev);
            setCalls((prev) => prev.map((c) => c.id === selectedId
              ? { ...c, returning_caller: true, client_id: evt.client_id } : c));
          }
          // Poor-man's autoscroll.
          setTimeout(() => {
            if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
          }, 20);
        } catch {}
      };
      ws.onclose = () => {};
      wsRef.current = ws;
    })();
    return () => { if (wsRef.current) { try { wsRef.current.close(); } catch {} } };
  }, [selectedId]);

  const active = useMemo(() => calls.filter((c) => c.status === 'in_progress'), [calls]);
  const recent = useMemo(() => calls.filter((c) => c.status !== 'in_progress').slice(0, 20), [calls]);

  return (
    <div className="space-y-6" data-testid="live-calls-page">
      <div>
        <h2 className="font-serif text-2xl text-brand-green">Live calls</h2>
        <p className="text-sm text-brand-green/70 mt-1">
          Watch the AI assistant answer, book, and transfer in real time.
        </p>
      </div>
      {error && <div className="text-xs text-red-700 bg-red-50 p-3 rounded">{error}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.5fr] gap-6">
        <div className="space-y-4" data-testid="live-calls-list">
          <Card className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Radio className="w-4 h-4 text-brand-gold" />
              <div className="text-sm font-semibold text-brand-green">Now on the line</div>
            </div>
            {active.length === 0 && <div className="text-xs text-brand-green/60">No live calls right now.</div>}
            {active.map((c) => (
              <CallRow key={c.id} call={c} active onSelect={() => setSelectedId(c.id)} isSelected={selectedId === c.id} />
            ))}
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <PhoneOff className="w-4 h-4 text-brand-green/60" />
              <div className="text-sm font-semibold text-brand-green">Recent</div>
            </div>
            {recent.length === 0 && <div className="text-xs text-brand-green/60">No recent calls yet.</div>}
            {recent.map((c) => (
              <CallRow key={c.id} call={c} onSelect={() => setSelectedId(c.id)} isSelected={selectedId === c.id} />
            ))}
          </Card>
        </div>

        <Card className="p-5 min-h-[420px]">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 flex-wrap">
              <PhoneCall className="w-4 h-4 text-brand-gold" />
              <div className="text-sm font-semibold text-brand-green">
                {selectedId ? 'Transcript' : 'Select a call to watch'}
              </div>
              {selectedDetail?.returning_caller && (
                <span data-testid="returning-caller-header-pill"
                      className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-semibold bg-emerald-100 text-emerald-800">
                  <UserCheck className="w-3 h-3" /> Returning caller
                </span>
              )}
            </div>
            {selectedDetail?.recording_url && (
              <a href={selectedDetail.recording_url} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1 text-xs text-brand-green hover:underline"
                 data-testid="live-calls-recording-link">
                <Play className="w-3 h-3" /> Play recording
              </a>
            )}
          </div>
          {!selectedId && <div className="text-xs text-brand-green/50">
            The transcript, tool calls, and Fair-Housing flags stream in live from the AI receptionist.
          </div>}
          <div ref={scrollRef} className="space-y-2 max-h-[520px] overflow-y-auto pr-2" data-testid="live-calls-transcript">
            {monitorEvents.map((e, i) => <EventLine key={i} evt={e} />)}
          </div>
        </Card>
      </div>
    </div>
  );
}

function CallRow({ call, active, onSelect, isSelected }) {
  return (
    <button
      onClick={onSelect}
      data-testid={`live-call-row-${call.id}`}
      className={`w-full text-left rounded-lg px-3 py-2 mb-1 transition-colors ${
        isSelected ? 'bg-brand-green text-brand-cream' : 'hover:bg-brand-cream'
      }`}
    >
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className={`font-medium ${isSelected ? 'text-brand-cream' : 'text-brand-green'}`}>
          {call.caller_from || 'Unknown caller'}
        </span>
        <div className="flex items-center gap-1 shrink-0">
          {call.returning_caller && (
            <span data-testid={`returning-caller-pill-${call.id}`}
                  className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                    isSelected ? 'bg-brand-cream text-brand-green' : 'bg-emerald-100 text-emerald-800'
                  }`}
                  title="This caller is already in your CRM">
              <UserCheck className="w-3 h-3" /> Returning
            </span>
          )}
          {active && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500 text-white animate-pulse">LIVE</span>}
        </div>
      </div>
      <div className={`text-[11px] mt-0.5 ${isSelected ? 'text-brand-cream/80' : 'text-brand-green/60'}`}>
        {fmtWhen(call.started_at)} · {fmtDuration(call.duration_seconds)} · {call.language || 'auto'}
      </div>
    </button>
  );
}

function EventLine({ evt }) {
  if (evt.type === 'user_transcript' || evt.type === 'assistant_transcript') {
    const isAI = evt.type === 'assistant_transcript';
    return (
      <div className={`flex ${isAI ? 'justify-start' : 'justify-end'}`}>
        <div className={`max-w-[85%] ${isAI ? 'text-left' : 'text-right'}`}>
          <div className={`text-[10px] uppercase tracking-wide font-semibold mb-1 ${
            isAI ? 'text-brand-gold' : 'text-brand-green/60'
          }`}>
            {isAI ? 'AI' : 'Caller'}
          </div>
          <div className={`inline-block rounded-2xl px-3 py-2 text-sm leading-relaxed text-left whitespace-pre-wrap ${
            isAI
              ? 'bg-brand-cream text-brand-green rounded-tl-sm'
              : 'bg-brand-green text-brand-cream rounded-tr-sm'
          }`}>
            {evt.text}
          </div>
        </div>
      </div>
    );
  }
  if (evt.type === 'tool_call') {
    return (
      <div className="text-xs bg-brand-cream/60 rounded p-2 flex items-start gap-2">
        <Wrench className="w-3 h-3 mt-0.5 text-brand-green" />
        <div>
          <div className="font-medium text-brand-green">Tool: {evt.name}</div>
          <div className="text-brand-green/70 mt-0.5">{JSON.stringify(evt.args || {})}</div>
          <div className="text-brand-green/60 mt-0.5">→ {JSON.stringify(evt.result || {})}</div>
        </div>
      </div>
    );
  }
  if (evt.type === 'fair_housing_incident') {
    return (
      <div className="text-xs bg-red-50 rounded p-2 flex items-start gap-2 text-red-800" data-testid="fair-housing-flag">
        <ShieldAlert className="w-3 h-3 mt-0.5" />
        <div>Fair Housing flag on {evt.role} — terms: {(evt.terms || []).join(', ')}</div>
      </div>
    );
  }
  if (evt.type === 'loop_detected') {
    return (
      <div className="text-xs bg-yellow-50 rounded p-2 flex items-start gap-2 text-yellow-900" data-testid="loop-detected-flag">
        <ShieldAlert className="w-3 h-3 mt-0.5" />
        <div>Loop detected — AI repeated a turn ({Math.round((evt.similarity || 0) * 100)}% overlap, {evt.seconds_ago}s ago). Escalating…</div>
      </div>
    );
  }
  if (evt.type === 'escalation_dial_started') {
    return (
      <div className="text-xs bg-brand-cream rounded p-2 flex items-start gap-2 text-brand-green">
        <ShieldAlert className="w-3 h-3 mt-0.5 text-brand-gold" />
        <div>Escalating to {evt.target} — {evt.reason}</div>
      </div>
    );
  }
  if (evt.type === 'escalation_callback_scheduled') {
    return (
      <div className="text-xs text-brand-green/80">
        Realtor didn&rsquo;t answer ({evt.dial_status}) — caller told they&rsquo;ll get a callback within the hour.
      </div>
    );
  }
  if (evt.type === 'warm_transfer_executed') {
    return <div className="text-xs text-brand-gold">→ Warm-transferred to {evt.target}</div>;
  }
  if (evt.type === 'recording_ready') {
    return <div className="text-xs text-brand-green/70">Recording ready</div>;
  }
  if (evt.type === 'started') {
    return <div className="text-xs text-brand-green/70">Call started — {evt.caller_from || 'Unknown'}</div>;
  }
  if (evt.type === 'returning_caller') {
    return (
      <div className="text-xs bg-emerald-50 rounded p-2 flex items-start gap-2 text-emerald-900"
           data-testid="returning-caller-event">
        <UserCheck className="w-3 h-3 mt-0.5" />
        <div>Returning caller recognized{evt.name ? ` — ${evt.name}` : ''}. AI will greet them by name.</div>
      </div>
    );
  }
  if (evt.type === 'ended') {
    return <div className="text-xs text-brand-green/70">Call ended ({fmtDuration(evt.duration_seconds)})</div>;
  }
  if (evt.type === 'interrupted') {
    return <div className="text-[11px] text-brand-green/50">— caller interrupted —</div>;
  }
  return null;
}
